import angular, {
    IAugmentedJQuery,
    IComponentController,
    IComponentOptions,
    IOnChangesObject,
    IScope,
    ITimeoutService,
    IWindowService,
} from 'angular';
import { StateService } from 'angular-ui-router';
import { FontLoader } from '../../../../tn.fonts/fontloader.service/fontloader.service';
import { IFontStyleModel, VFCustomAxes } from '../../../classes';
import { debounce, getFontName } from '../../../functions';
import { applyCase } from '../../../functions/applyCase';
import { getCssFontVariationSettings } from '../../../functions/getCssFontVariationSettings';
import { SpecimenService, TextCase } from '../../../providers';
import template from './specimen.html';

export class SpecimenComponentBindings {
    font?: IFontStyleModel;
    // specify text explicitly, this will override autogeneration
    customText?: string;
    // size in pixels
    size = 0;
    // whole number percentage to scale the font-size by.
    scale = 100;
    // set text case for sample.
    case: TextCase = 'sentence';
    variationSettings?: Record<VFCustomAxes, number>;
    // force specimen text generation.
    generate = false;
    specimenLinkTo: 'family' | 'style' | 'none' = 'none';
}

export class SpecimenComponent
    extends SpecimenComponentBindings
    implements IComponentController
{
    // #region locals

    // this will be set when the font is actually loaded in the browser and we can calculate measurements on it.
    _font?: IFontStyleModel;
    _text = '';
    _cssStyle = 'font-family: "Adobe Blank";';
    _size = 56;
    _loadingFont = false;
    _loadedFont = false;
    _loadFontFileFailed = false;
    _windowWidth = this.$window.innerWidth;
    srefParams?: Record<string, string | undefined>;

    // #endregion

    static $inject = [
        '$window',
        '$timeout',
        'specimenService',
        '$element',
        '$scope',
        'tn.fontloader.service',
        '$state',
    ];
    constructor(
        protected $window: IWindowService,
        protected $timeout: ITimeoutService,
        protected specimenService: SpecimenService,
        protected $element: IAugmentedJQuery,
        protected $scope: IScope,
        protected fontLoader: FontLoader,
        protected $state: StateService
    ) {
        super();
    }

    onresize = debounce(() => this.onResize(), 250, false);

    $onInit(): void {
        // console.log('SpecimenComponent.$onInit', this);
        angular.element(this.$window).on('resize', this.onresize);

        switch (this.specimenLinkTo) {
            case 'family':
                this.srefParams = {
                    familyName: this.font?.family_slug,
                    foundryEESubdomain: this.font?.ee_subdomain,
                };
                break;
            case 'style':
                this.srefParams = {
                    familyName: this.font?.family_slug,
                    styleName: this.font?.slug,
                    foundryEESubdomain: this.font?.ee_subdomain,
                };
                break;
            default:
                break;
        }
        this.setText();
    }

    $onDestroy(): void {
        angular.element(this.$window).off('resize', this.onresize);
    }

    $onChanges(changes: IOnChangesObject): void {
        // console.log('SpecimenComponent.$onChanges', changes);
        if (
            changes.font &&
            changes.font.currentValue != changes.font.previousValue
        ) {
            this.font && this.fontLoader.get(this.font, !!this.customText);
        }
        if (
            changes.customText &&
            changes.customText.currentValue !== changes.customText.previousValue
        ) {
            this.setText();
            if (this.font) this.fontLoader.get(this.font, !!this.customText);
        }
        if (changes.scale || changes.variationSettings || changes.font) {
            this.setCssStyle();
        }
    }

    getScaledSize(): number {
        const scale = (this.scale || 100) / 100;
        const size = this.getSize();
        return Math.round(size * scale);
    }

    getSize(): number {
        if (this.size) {
            return this.size;
        }

        // when displaying custom text, we make it all match.
        if (this.customText || !this.font) {
            return 56; // 3.5 rem @ 16px
        }

        // to get the max_recommended_size.
        if (this.font.max_recommended_size) {
            return Math.min(this.font.max_recommended_size, 56);
        }

        // get the min recommended_size
        if (this.font.min_recommended_size) {
            return Math.min(this.font.min_recommended_size, 56);
        }

        // try to figure out based on OS/2 recommended sizes.
        const max_recommended_size = Math.max(...this.font.recommended_size);

        // we display using the biggest recommended size by default
        switch (max_recommended_size) {
            case 200:
                return 12;
            case 300:
                return 14;
            case 400:
            case 700:
            case 800:
            default:
                return 56;
        }
    }

    setCssStyle(): void {
        if (!this.font) return;
        const cssName = getFontName(this.font);
        const _cssFontFamily = `"${cssName}", "Adobe Blank"`;
        const size = this.getScaledSize();
        // if the style has an axis property, then we were passed an instance and not
        // the variable, so use the values from the instance instead of dynamically supplied..
        const variationSettings =
            this.font?.axis?.axes || this.variationSettings;
        const variation = getCssFontVariationSettings(variationSettings);
        this._cssStyle = `font-family: ${_cssFontFamily}; font-size: ${size}px; font-variation-settings: ${variation}; overflow: hidden; text-overflow: ellipsis;`;
    }

    // update the specimen text.
    setText(): void {
        // if we've got static specimens, let's return a static fit.
        if (this.customText && !this.generate) {
            this._text = this.customText;
            return;
        }

        if (this.font && this.font.specimen_text && !this.generate) {
            this._text = this.doCase(this.font.specimen_text);
            return;
        }

        // fix to 60 characters wide.
        const width = 60;

        // generate a 0
        const text = this.specimenService.getSingleLineSpecimen(
            width,
            this.case
        );
        this._text = this.doCase(text);
    }

    doCase(text: string): string {
        return applyCase(this.case, text);
    }

    onResize(): void {
        // console.log('onResize', this._windowWidth, this.$window.innerWidth);
        if (this._windowWidth === this.$window.innerWidth) {
            return;
        }
        this._windowWidth = this.$window.innerWidth;
        this.$scope.$digest();
    }
}

export const SpecimenComponentOptions: IComponentOptions = {
    controller: SpecimenComponent,
    bindings: {
        // font family to use
        font: '<',
        // specify text explicitly
        customText: '<?',
        // size in pixels
        size: '<?',
        // % scale to apply to size
        scale: '<?',
        // variation settings object
        variationSettings: '<?',
        // set text case for generated sample, only used if customText is not set or generate is true
        case: '<?',
        // for text generation, even if customText is set, or there is a pangram.
        generate: '<?',
        // optional destination to link to on specimen clicked. options: family, style, none
        specimenLinkTo: '<?',
    },
    template: template,
};
